import { Company } from "../../slices/companiesSlice";
import React from "react";
import CompanyManagementActionMenu from "../CompanyManagementActionMenu/CompanyManagementActionMenu";
import { Column } from "@/components/Datatable/types";
import { CheckCircle2, XCircle, Minus, Loader2 } from "lucide-react";

interface CreateCompaniesColumnsProps {
  handleEdit: (uuid: string) => void;
  handleViewSyncLogs: (company: Company) => void;
  handleSyncCompany: (company: Company) => void;
  syncingCompanyIds: number[];
}

const SyncStatusIcon: React.FC<{ appResult: any; isLoading?: boolean }> = ({
  appResult,
  isLoading,
}) => {
  // Show spinner if this app hasn't completed yet but sync is in progress
  if (isLoading && !appResult) {
    return <Loader2 className="h-4 w-4 text-yellow-500 animate-spin" />;
  }

  if (!appResult) {
    return <Minus className="h-4 w-4 text-gray-400" />;
  }

  const success = appResult.response?.success || appResult.response?.Success;

  if (success) {
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }

  return <XCircle className="h-4 w-4 text-red-500" />;
};

export function createCompaniesColumns({
  handleEdit,
  handleViewSyncLogs,
  handleSyncCompany,
  syncingCompanyIds,
}: CreateCompaniesColumnsProps): Column<Company>[] {
  return [
    {
      header: "ID",
      accessorKey: "id",
      enableSorting: true,
    },
    {
      header: "Company Name",
      accessorKey: "companyName",
      enableSorting: true,
    },
    {
      header: "Intacct ID",
      accessorKey: "intacctId",
      enableSorting: true,
    },
    {
      header: "Salesforce ID",
      accessorKey: "salesforceId",
      enableSorting: true,
    },
    {
      header: "Stochastic Client",
      enableSorting: true,
      accessorKey: "marketingEnabled",
      cell: ({ row }) => (row.original.marketingEnabled ? "Yes" : "No"),
    },
    {
      id: "syncStatus",
      header: "Sync Status",
      cell: ({ row }) => {
        const company = row.original;
        const syncResults = company.remoteSyncLog?.syncResults;
        const isInProgress =
          company.remoteSyncLog?.status === "in_progress" ||
          syncingCompanyIds.includes(company.id);

        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1" title="Salesforce">
              <span className="text-xs text-gray-500">SF</span>
              <SyncStatusIcon
                appResult={syncResults?.salesforce}
                isLoading={isInProgress}
              />
            </div>
            <div className="flex items-center gap-1" title="MemberPortal">
              <span className="text-xs text-gray-500">MP</span>
              <SyncStatusIcon
                appResult={syncResults?.memberportal}
                isLoading={isInProgress}
              />
            </div>
            <div className="flex items-center gap-1" title="PartnerNetwork">
              <span className="text-xs text-gray-500">PN</span>
              <SyncStatusIcon
                appResult={syncResults?.partnernetwork}
                isLoading={isInProgress}
              />
            </div>
            <div className="flex items-center gap-1" title="Scoreboard">
              <span className="text-xs text-gray-500">SB</span>
              <SyncStatusIcon
                appResult={syncResults?.scoreboard}
                isLoading={isInProgress}
              />
            </div>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const company = row.original;
        return (
          <CompanyManagementActionMenu
            company={company}
            onEdit={handleEdit}
            onViewSyncLogs={() => handleViewSyncLogs(company)}
            onSyncCompany={() => handleSyncCompany(company)}
          />
        );
      },
    },
  ];
}
